/**
 * AsymmetryEducationPanel — V23.12
 *
 * Bloc pédagogique dans la Cartographie des dépendances qui :
 *   1. Vulgarise la formule Asymétrie × Fréquence × Valeur × Remplacement
 *   2. Pose les 3 questions pratiques pour positionner un acteur
 *   3. Calcule "Si cet acteur disparaît…" pour un acteur sélectionné
 *      (basé sur les relations entrantes existantes)
 *   4. Donne accès direct aux modules liés (Asymétries / Origination / Chaînage)
 *
 * Mode additif strict : aucune fonctionnalité existante n'est retirée.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Lightbulb, ChevronDown, ChevronRight, Sparkles, AlertTriangle,
  TrendingUp, TrendingDown, ExternalLink, Target, Crown,
} from "lucide-react";

const FORMULA_TERMS = [
  {
    key: "asymetrie",
    label: "Asymétrie",
    icon: "🔺",
    color: "#EF9F27",
    short: "Ce que j'ai et qu'on a difficilement ailleurs.",
    long: "Information rare, réseau exclusif, capital, technologie propriétaire, réglementation, confiance accumulée, etc. Plus c'est rare, plus c'est précieux.",
    example: "Ex : un courtier en assurance qui connaît personnellement les 200 entrepreneurs tech de Genève. Son réseau = son asymétrie principale.",
  },
  {
    key: "frequence",
    label: "Fréquence",
    icon: "🔁",
    color: "#378ADD",
    short: "Combien de fois par an mon offre est sollicitée.",
    long: "Une asymétrie sollicitée 50 fois par an vaut plus qu'une asymétrie sollicitée 2 fois — même si chaque transaction est plus petite.",
    example: "Ex : un dentiste a une fréquence élevée (clients réguliers), un notaire en fusion-acquisition une fréquence faible mais ticket énorme.",
  },
  {
    key: "valeur",
    label: "Valeur",
    icon: "💎",
    color: "#1D9E75",
    short: "Le ticket moyen de chaque interaction (en CHF / EUR).",
    long: "Pour un courtier institutionnel : valeur d'une transaction = 200k. Pour un boulanger : 8 CHF par client. La valeur scale avec la criticité du besoin.",
    example: "Ex : un cabinet d'avocats fiscaliste qui gère une optimisation patrimoniale = ticket 50k. Une boulangerie sur place = ticket 8 CHF.",
  },
  {
    key: "remplacement",
    label: "Remplacement",
    icon: "🛡️",
    color: "#E24B4A",
    short: "Difficulté pour le client de me remplacer par un autre.",
    long: "C'est l'amplificateur final. Si on peut me remplacer en 1 semaine, mon score s'effondre. Si on ne peut pas (réglementation, contrats long-terme, données captives, switching costs), la rente devient durable.",
    example: "Ex : SIX SIS (dépositaire central suisse) — quasi-impossible à remplacer. AWS — coûteux à remplacer. Un graphiste freelance — remplacé en 24h.",
  },
];

const POSITION_QUESTIONS = [
  {
    icon: TrendingUp, color: "#1D9E75", label: "Amont",
    q: "Quelle ressource, accès ou information je contrôle à l'entrée du flux ?",
    examples: "Données propriétaires · base de prospects · matière première · brevets · licences",
  },
  {
    icon: TrendingDown, color: "#378ADD", label: "Aval",
    q: "Quelle distribution, décision finale ou sortie je contrôle ?",
    examples: "Audience captive · marque connue du client final · contrats de distribution exclusifs · canaux directs",
  },
  {
    icon: Crown, color: "#EF9F27", label: "Soutien du soutien",
    q: "De quel intermédiaire suis-je l'infrastructure invisible mais indispensable ?",
    examples: "API utilisée par toutes les fintechs · audit obligatoire avant cotation · système de paiement",
  },
];

export default function AsymmetryEducationPanel({
  selectedActor,
  actors = [],
  relations = [],
  className = "",
  onHighlightTerm,
}) {
  const navigate = useNavigate();
  const [openTerm, setOpenTerm] = useState(null);

  useEffect(() => {
    onHighlightTerm?.(openTerm);
  }, [openTerm, onHighlightTerm]);

  useEffect(() => () => onHighlightTerm?.(null), [onHighlightTerm]);
  const [showPedago, setShowPedago] = useState(true);

  const ASYM_LABELS = {
    information: "Information", reseau: "Réseau", capital: "Capital",
    technologie: "Technologie", reglementaire: "Réglementaire", confiance: "Confiance",
    infrastructure: "Infrastructure", temps: "Temps", execution: "Exécution", coordination: "Coordination",
  };

  // Explication CONTEXTUALISÉE à la carte réelle (pas un exemple générique).
  const mapContext = useMemo(() => {
    const N = actors.length;
    if (N === 0) {
      return () => "Ajoutez des acteurs à votre cartographie pour voir ce terme appliqué à VOTRE situation.";
    }
    const scored = [...actors].sort((a, b) => (b.asymmetry_score || 0) - (a.asymmetry_score || 0));
    const top = scored[0];
    const critical = actors.filter((a) => a.criticite === "haute" || a.criticite === "critique");
    // bottleneck = acteur avec le plus de dépendances entrantes
    const incomingCount = {};
    relations.forEach((r) => { const tid = r.to_id || r.target_id; incomingCount[tid] = (incomingCount[tid] || 0) + 1; });
    let bottleneck = null, maxIn = 0;
    actors.forEach((a) => { if ((incomingCount[a.id] || 0) > maxIn) { maxIn = incomingCount[a.id] || 0; bottleneck = a; } });
    const focus = selectedActor || top;

    return (key) => {
      switch (key) {
        case "asymetrie":
          return top && (top.asymmetry_score || 0) > 0
            ? `Sur votre carte : « ${top.name} » détient l'asymétrie la plus forte (${ASYM_LABELS[top.asymmetry_type] || "type non défini"}, score ${top.asymmetry_score}/100). C'est votre point de rente principal.`
            : `Vos ${N} acteur(s) n'ont pas encore de score d'asymétrie renseigné — cliquez sur un acteur pour définir son type et son score.`;
        case "frequence":
          return bottleneck && maxIn > 0
            ? `Sur votre carte : « ${bottleneck.name} » est un point de passage (${maxIn} dépendance${maxIn > 1 ? "s" : ""} entrante${maxIn > 1 ? "s" : ""}) — c'est là que la fréquence de sollicitation se concentre.`
            : `Classez vos ${N} acteur(s) par nombre d'interactions/an : reliez-les (glisser une poignée) pour révéler les points de passage à forte fréquence.`;
        case "valeur":
          return critical.length > 0
            ? `Sur votre carte : ${critical.length} acteur(s) en criticité haute/critique (${critical.slice(0, 2).map((a) => a.name).join(", ")}${critical.length > 2 ? "…" : ""}) — ce sont eux qui portent le ticket le plus élevé.`
            : `Aucun acteur critique identifié pour l'instant : marquez la criticité de vos ${N} acteur(s) pour faire ressortir où se loge la valeur.`;
        case "remplacement":
          return focus
            ? `Sur votre carte : « ${focus.name} » (criticité ${focus.criticite || "à définir"}) est ${focus.criticite === "critique" ? "quasi-impossible" : "difficile"} à remplacer. Plus la criticité est haute, plus la rente est durable.`
            : `Interrogez chaque acteur : peut-on le remplacer en 1 semaine ? Si non (réglementation, contrats, données captives), la rente devient durable.`;
        default:
          return "";
      }
    };
  }, [actors, relations, selectedActor]);

  // ----- Analyse "Si cet acteur disparaît" -----
  const disappearanceAnalysis = useMemo(() => {
    if (!selectedActor) return null;
    const incoming = relations.filter((r) => r.target_id === selectedActor.id);
    const dependents = incoming.map((r) => actors.find((a) => a.id === r.source_id)).filter(Boolean);
    const criticalDependents = dependents.filter((a) => a.criticite === "haute" || a.criticite === "critique");
    // Beneficiaires : substituts indirects = ceux qui ont une asymétrie similaire et qui pourraient capter la rente
    const beneficiaries = actors.filter((a) =>
      a.id !== selectedActor.id &&
      a.asymmetry_type === selectedActor.asymmetry_type &&
      (a.asymmetry_score || 0) >= (selectedActor.asymmetry_score || 0) * 0.5
    );
    return {
      incoming_count: incoming.length,
      dependents_count: dependents.length,
      critical_dependents_count: criticalDependents.length,
      critical_dependents: criticalDependents.slice(0, 3),
      beneficiaries: beneficiaries.slice(0, 3),
    };
  }, [selectedActor, actors, relations]);

  return (
    <div className={`noam-card p-4 space-y-3 ${className}`} data-testid="asym-edu-panel">
      {/* HEADER PÉDAGOGIQUE */}
      <button
        type="button"
        onClick={() => setShowPedago((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="asym-edu-toggle"
      >
        <Lightbulb className="w-3 h-3 text-[#EF9F27]" />
        <span className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-bold flex-1">
          Comprendre les asymétries — guide pratique
        </span>
        {showPedago ? <ChevronDown className="w-3 h-3 text-zinc-500" /> : <ChevronRight className="w-3 h-3 text-zinc-500" />}
      </button>

      {showPedago && (
        <>
          {/* Formule décomposée */}
          <div className="space-y-2">
            <p className="text-[11px] text-zinc-300 leading-relaxed">
              <strong className="text-white">La rente naît partout où une asymétrie devient non-remplaçable.</strong>
              {" "}On la calcule en multipliant quatre termes — cliquez sur chacun pour comprendre :
            </p>
            <div className="flex flex-col gap-1.5">
              {FORMULA_TERMS.map((t) => {
                const open = openTerm === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setOpenTerm(open ? null : t.key)}
                    className={`w-full rounded-md px-2.5 py-2 border text-left transition flex items-start gap-2.5 ${
                      open ? "border-white/20 bg-white/5" : "border-white/5 bg-white/[0.02] hover:bg-white/[0.04]"
                    }`}
                    style={{ borderColor: open ? t.color : undefined }}
                    data-testid={`asym-formula-${t.key}`}
                  >
                    <span className="text-base leading-none shrink-0" style={{ color: t.color }}>{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-bold leading-tight" style={{ color: t.color }}>
                        {t.label}
                      </div>
                      <div className="text-[10px] text-zinc-400 mt-0.5 leading-snug">{t.short}</div>
                    </div>
                    <span className="shrink-0 mt-0.5 text-zinc-600">
                      {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    </span>
                  </button>
                );
              })}
            </div>
            {openTerm && (
              <div className="bg-white/[0.03] border border-white/5 rounded-md p-2.5 text-[11px] text-zinc-300 leading-relaxed space-y-1.5" data-testid={`asym-formula-detail-${openTerm}`}>
                {(() => {
                  const t = FORMULA_TERMS.find((x) => x.key === openTerm);
                  return (
                    <>
                      <p>{t.long}</p>
                      <p className="text-[10px] leading-relaxed border-l-2 pl-2" style={{ borderColor: t.color, color: "#d4d4d8" }} data-testid={`asym-formula-context-${openTerm}`}>
                        {mapContext(t.key)}
                      </p>
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* 3 questions de positionnement */}
          <div className="border-t border-white/5 pt-3 space-y-1.5">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
              Trois questions pour positionner un acteur
            </div>
            {POSITION_QUESTIONS.map((q, i) => {
              const Icon = q.icon;
              return (
                <div key={i} className="text-[11px] text-zinc-300 leading-relaxed flex gap-2 items-start">
                  <Icon className="w-3 h-3 shrink-0 mt-0.5" style={{ color: q.color }} />
                  <div className="flex-1">
                    <div><strong style={{ color: q.color }}>{q.label} :</strong> {q.q}</div>
                    <div className="text-[10px] text-zinc-500 italic">{q.examples}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* SECTION DYNAMIQUE — Acteur sélectionné */}
      {selectedActor && (
        <div className="border-t border-white/5 pt-3 space-y-2.5" data-testid="asym-actor-analysis">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">
            Analyse de l'acteur — {selectedActor.name}
          </div>

          {/* Question 1 : Si cet acteur disparaît ? */}
          {disappearanceAnalysis && disappearanceAnalysis.dependents_count > 0 && (
            <div className="bg-[#E24B4A]/5 border border-[#E24B4A]/20 rounded-md p-2.5">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle className="w-3 h-3 text-[#E24B4A]" />
                <span className="text-[10px] uppercase tracking-wider text-[#E24B4A] font-bold">
                  Si cet acteur disparaît…
                </span>
              </div>
              <p className="text-[11px] text-zinc-300 leading-relaxed">
                <strong className="text-white">{disappearanceAnalysis.dependents_count} acteur{disappearanceAnalysis.dependents_count > 1 ? "s" : ""}</strong> dépend
                {disappearanceAnalysis.dependents_count > 1 ? "ent" : ""} de lui directement.
                {disappearanceAnalysis.critical_dependents_count > 0 && (
                  <> Dont <strong className="text-[#E24B4A]">{disappearanceAnalysis.critical_dependents_count}</strong> avec une criticité haute ou critique.</>
                )}
              </p>
              {disappearanceAnalysis.critical_dependents.length > 0 && (
                <ul className="mt-1.5 text-[10px] text-zinc-400 space-y-0.5">
                  {disappearanceAnalysis.critical_dependents.map((d) => (
                    <li key={d.id} className="flex items-center gap-1">
                      <span className="text-[#E24B4A]">•</span> {d.name}
                    </li>
                  ))}
                </ul>
              )}
              {disappearanceAnalysis.beneficiaries.length > 0 && (
                <div className="mt-2 pt-2 border-t border-white/5">
                  <div className="text-[10px] text-zinc-500 mb-0.5">Qui hérite de la rente ?</div>
                  <ul className="text-[10px] text-zinc-300 space-y-0.5">
                    {disappearanceAnalysis.beneficiaries.map((b) => (
                      <li key={b.id} className="flex items-center gap-1">
                        <Target className="w-2.5 h-2.5 text-[#EF9F27]" /> {b.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Question 2 : Valeur du marché — amont vs aval */}
          <div className="bg-white/[0.02] border border-white/5 rounded-md p-2.5">
            <div className="flex items-center gap-1.5 mb-1.5">
              <Sparkles className="w-3 h-3 text-[#EF9F27]" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-300 font-bold">
                Quelle est la valeur si je travaille en amont ou en aval pour lui ?
              </span>
            </div>
            <p className="text-[11px] text-zinc-400 leading-relaxed">
              Pour mesurer précisément la taille de marché autour de cet acteur et la valeur potentielle
              que vous pourriez capter en amont ou en aval, utilisez le module <strong>Chaînage causal</strong>
              {" "}— il calcule via Tavily + NOAM le marché local/mondial et votre % de capture atteignable.
            </p>
            <button
              type="button"
              onClick={() => navigate("/app/chainage")}
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-[#EF9F27] hover:underline font-semibold"
              data-testid="asym-go-chainage"
            >
              Modéliser dans Chaînage causal <ExternalLink className="w-2.5 h-2.5" />
            </button>
          </div>

          {/* Cross-module navigation */}
          <div className="grid grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => navigate(`/app/asymetries?actor=${encodeURIComponent(selectedActor.name)}`)}
              className="text-[10px] px-2 py-1.5 rounded border border-[#EF9F27]/30 bg-[#EF9F27]/5 text-[#EF9F27] hover:bg-[#EF9F27]/10 inline-flex items-center justify-center gap-1"
              data-testid="asym-cross-asymetries"
            >
              <Crown className="w-2.5 h-2.5" /> Asymétries
            </button>
            <button
              type="button"
              onClick={() => navigate(`/app/origination?from=cartographie&actor=${encodeURIComponent(selectedActor.name)}`)}
              className="text-[10px] px-2 py-1.5 rounded border border-[#1D9E75]/30 bg-[#1D9E75]/5 text-[#1D9E75] hover:bg-[#1D9E75]/10 inline-flex items-center justify-center gap-1"
              data-testid="asym-cross-origination"
            >
              <Target className="w-2.5 h-2.5" /> Origination
            </button>
            <button
              type="button"
              onClick={() => navigate(`/app/chainage?actor=${encodeURIComponent(selectedActor.name)}`)}
              className="text-[10px] px-2 py-1.5 rounded border border-[#378ADD]/30 bg-[#378ADD]/5 text-[#378ADD] hover:bg-[#378ADD]/10 inline-flex items-center justify-center gap-1"
              data-testid="asym-cross-chainage"
            >
              <TrendingUp className="w-2.5 h-2.5" /> Chaînage
            </button>
          </div>
        </div>
      )}

      {!selectedActor && showPedago && (
        <div className="border-t border-white/5 pt-3 text-[10px] text-zinc-500 italic leading-relaxed">
          💡 Sélectionnez un acteur dans la cartographie pour voir l'analyse "Si X disparaît", la valeur amont/aval
          et les liens vers les modules Asymétries, Origination et Chaînage.
        </div>
      )}
    </div>
  );
}
